import { formatDistanceToNowStrict } from 'date-fns';
import { Hourglass, MessageSquare } from 'lucide-react';
import { formatDateTime, timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Task } from './types';

/** How long a task has been waiting on its owner, since they were given it. */
export function waitingFor(task: Task): { since: string; hours: number } {
  const since = task.assignedAt ?? task.createdAt;
  return { since, hours: (Date.now() - new Date(since).getTime()) / 3_600_000 };
}

/**
 * Where the work stands, in words: finished, started, updated, or — the one
 * this exists for — sitting untouched, and for how long.
 *
 * The untouched case warms from muted to amber after a day and to red after
 * three, and says the duration outright, so it never relies on the colour.
 */
export function WorkState({ task }: { task: Task }) {
  if (task.status === 'DONE') {
    return task.completedAt ? (
      <span className="text-emerald-700">Done {timeAgo(task.completedAt)}</span>
    ) : (
      <span className="text-emerald-700">Done</span>
    );
  }
  if (task.status === 'CANCELLED') return <span>Cancelled</span>;
  if (task.status === 'IN_PROGRESS') {
    return (
      <span className="text-sky-700">
        In progress{task.lastRemarkAt ? ` · updated ${timeAgo(task.lastRemarkAt)}` : ''}
      </span>
    );
  }

  // Still to do, but somebody has written an update: that is not "no action".
  if (task.lastRemarkAt) {
    return (
      <span className="flex items-center gap-0.5">
        <MessageSquare className="h-3 w-3" /> Updated {timeAgo(task.lastRemarkAt)}
      </span>
    );
  }

  const { since, hours } = waitingFor(task);
  return (
    <span
      className={cn(
        'flex items-center gap-0.5',
        hours >= 72 ? 'font-medium text-red-700' : hours >= 24 ? 'font-medium text-amber-700' : '',
      )}
      title={`Given to ${task.assignee?.name ?? 'nobody'} ${formatDateTime(since)}, not started yet`}
    >
      <Hourglass className="h-3 w-3" /> No action for {formatDistanceToNowStrict(new Date(since))}
    </span>
  );
}
